import React from "react";

// Third-party
import ReactDOM from "react-dom";
import { BrowserRouter } from "react-router-dom";
import { SnackbarProvider } from "notistack";
import { Integrations } from "@sentry/tracing";
import * as Sentry from "@sentry/react";

// Material UI
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import {
  StylesProvider,
  createGenerateClassName,
  ThemeProvider
} from "@material-ui/core/styles";
import theme from "./theme";

// Components
import App from "./App";
import ErrorBoundaryWrapper from "features/Utils/ErrorBoundary/ErrorBoundaryWrapper";

// Context
import AuthProvider from "./AuthProvider";
import AppProvider from "AppProvider";
import LocalSettingsProvider from "context/LocalSettingsContext";
import CommentsProvider from "context/CommentsContext";
import CapabilitiesProvider from "context/CapabilitiesContext";
import CookiesProvider from "context/CookiesContext";

// Other
import "./index.css";
import "./i18n";
import getenvvar from "utils/Env";
import { beforeSend, isIgnoredRejection, isSentryEnabled } from "utils/Sentry";
import * as serviceWorker from "./serviceWorker";
import CostModelsProvider from "context/CostModelsContext";
import { PATHFINDER_VERSION } from "assets/global";

// Constants
const Sentry_DSN = getenvvar("REACT_APP_API_SENTRY_DSN");
const Sentry_Release = getenvvar("REACT_APP_API_SENTRY_RELEASE");
const APP_VERSION = PATHFINDER_VERSION;
const generateClassName = createGenerateClassName({
  productionPrefix: "path"
});

// Notistack Config
const notistackRef = React.createRef();
const onClickDismiss = key => () => {
  notistackRef.current.closeSnackbar(key);
};

// Sentry Initialization
Sentry.init({
  dsn: Sentry_DSN || null,
  enabled: isSentryEnabled(process.env.NODE_ENV, window.location.hostname),
  integrations: [new Integrations.BrowserTracing({ tracingOrigins: ["*"] })],
  // We recommend adjusting this value in production, or using tracesSampler
  // for finer control
  tracesSampleRate: 0.1,
  release: Sentry_Release,
  beforeSend
});

// Global variable to store current user (will be set by ErrorBoundaryWrapper)
window.__PATHFINDER_CURRENT_USER__ = null;

// Global error handlers for catching errors outside React component tree
window.addEventListener("error", event => {
  // Capture synchronous JavaScript errors
  Sentry.withScope(scope => {
    // Add app version
    scope.setTag("app_version", APP_VERSION);
    scope.setContext("app", { version: APP_VERSION });

    // Add user if available
    if (window.__PATHFINDER_CURRENT_USER__) {
      scope.setUser({ username: window.__PATHFINDER_CURRENT_USER__ });
    }

    // Add error context
    scope.setContext("windowError", {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    });

    Sentry.captureException(event.error);
  });
});

window.addEventListener("unhandledrejection", event => {
  // Skip known, non-actionable rejections (e.g. token expiry)
  if (isIgnoredRejection(event.reason)) {
    return;
  }

  // Capture unhandled promise rejections
  Sentry.withScope(scope => {
    // Add app version
    scope.setTag("app_version", APP_VERSION);
    scope.setContext("app", { version: APP_VERSION });

    // Add user if available
    if (window.__PATHFINDER_CURRENT_USER__) {
      scope.setUser({ username: window.__PATHFINDER_CURRENT_USER__ });
    }

    // Add rejection context
    scope.setContext("unhandledRejection", {
      reason: String(event.reason)
    });

    Sentry.captureException(event.reason);
  });
});

ReactDOM.render(
  <React.StrictMode>
    <StylesProvider generateClassName={generateClassName}>
      <ThemeProvider theme={theme}>
        <CookiesProvider>
          <AuthProvider>
            <CapabilitiesProvider>
              <BrowserRouter>
                <SnackbarProvider
                  classes={{
                    containerRoot: "snackbar-container"
                  }}
                  preventDuplicate
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "center"
                  }}
                  ref={notistackRef}
                  action={key => (
                    <IconButton
                      color="inherit"
                      size="small"
                      onClick={onClickDismiss(key)}
                      aria-label="close"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                >
                  <ErrorBoundaryWrapper>
                    <LocalSettingsProvider>
                      <CommentsProvider>
                        <AppProvider>
                          <CostModelsProvider>
                            <App />
                          </CostModelsProvider>
                        </AppProvider>
                      </CommentsProvider>
                    </LocalSettingsProvider>
                  </ErrorBoundaryWrapper>
                </SnackbarProvider>
              </BrowserRouter>
            </CapabilitiesProvider>
          </AuthProvider>
        </CookiesProvider>
      </ThemeProvider>
    </StylesProvider>
  </React.StrictMode>,
  document.getElementById("root")
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
export { theme };
